import { cs } from '../../../lib/currency';
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Edit, CheckCircle, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { extractErrorMessage } from './utils.jsx';

/**
 * Edit invoice price + admin notes (audit-logged on the backend).
 */
export default function EditInvoiceDialog({ open, onOpenChange, invoice, onEdited }) {
  const [form, setForm] = useState({ amount: '', notes: '', stripe_payment_intent_id: '' });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      setForm({
        amount: invoice.amount?.toString() || '',
        notes: invoice.admin_notes || '',
        stripe_payment_intent_id: invoice.stripe_payment_intent_id || '',
      });
    }
  }, [open, invoice]);

  const handleSubmit = async (forceOverridePaid = false) => {
    if (!invoice) return;
    setProcessing(true);
    try {
      await api.post('/admin/invoices/edit', {
        // For enrollment rows the row `id` is the monthly_invoice id
        // (payments_invoice_list_helpers exposes the enrollment id as
        // `entity_id`). The backend edit endpoint's enrollment branch
        // looks up `enrollments` by invoice_id, so send entity_id there
        // to avoid a spurious 404 "Enrollment not found". Other invoice
        // types keep using `id` (which is their own entity id).
        invoice_id: invoice.type === 'enrollment'
          ? (invoice.entity_id || invoice.id)
          : invoice.id,
        invoice_type: invoice.type,
        amount: form.amount ? parseFloat(form.amount) : null,
        notes: form.notes || null,
        // iter307ba — For enrollment-type rows the displayed amount lives
        // on the underlying monthly_invoice, not on the enrollment doc.
        // Pass the MI id so the backend updates the exact cycle the
        // admin is viewing (falls back to latest-open MI server-side).
        monthly_invoice_id: invoice.monthly_invoice_id || null,
        // 2026-06 — Link/relink a Stripe PaymentIntent to an invoice
        // recorded without one (older tournament/manual rows). Only send
        // when the admin actually changed it, so untouched edits don't
        // rewrite the field. Empty string unlinks.
        ...(form.stripe_payment_intent_id !== (invoice.stripe_payment_intent_id || '')
          ? { stripe_payment_intent_id: form.stripe_payment_intent_id.trim() }
          : {}),
        // iter307bk — Paid invoices are immutable by default. Backend
        // returns 409 `paid_invoice_immutable` unless this flag is set.
        force_override_paid: forceOverridePaid,
      });
      toast.success('Invoice updated successfully');
      onOpenChange(false);
      onEdited && onEdited();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // iter307bk — Surface the immutable-paid 409 as a confirmation
      // prompt: admin must explicitly choose to override. Original
      // amount is preserved in `original_amount` for audit either way.
      if (detail?.code === 'paid_invoice_immutable') {
        const ok = window.confirm(
          `This invoice is already ${detail.current_status} (${cs()}${(detail.current_amount ?? 0).toFixed(2)}).\n\n` +
          `Editing a settled invoice rewrites the historical record and updates the parent's Total Paid. ` +
          `The original amount will be preserved in the audit trail.\n\n` +
          `Continue and override?`
        );
        if (ok) {
          setProcessing(false);
          return handleSubmit(true);   // retry with override
        }
      } else {
        toast.error(extractErrorMessage(err, 'Failed to update invoice'));
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-blue-600" />
            Edit Invoice
          </DialogTitle>
          <DialogDescription>
            Update the invoice amount or admin notes. Changes are
            recorded in the audit log.
          </DialogDescription>
        </DialogHeader>
        {invoice && (
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg space-y-1">
              <p className="font-medium">{invoice.description}</p>
              <p className="text-sm text-slate-500">{invoice.parent_name}</p>
              <p className="text-sm text-slate-500">Current: {cs()}{invoice.amount?.toFixed(2)}</p>
              {invoice.status === 'partial' && (
                <p
                  className="text-xs text-amber-700"
                  data-testid="edit-invoice-partial-breakdown"
                >
                  Credit applied: {cs()}{Number(invoice.credit_applied || 0).toFixed(2)}
                  {' · '}Balance due: <strong>{cs()}{Number(invoice.balance_due || 0).toFixed(2)}</strong>
                </p>
              )}
            </div>

            <div>
              <Label>New Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder={invoice.amount?.toFixed(2)}
              />
              <p className="text-xs text-slate-500 mt-1">
                {invoice.status === 'partial'
                  ? `This is the full invoice total. The credit already applied (${cs()}${Number(invoice.credit_applied || 0).toFixed(2)}) stays put; the balance will recalculate from the new total.`
                  : 'Leave blank to keep current amount'}
              </p>
            </div>

            <div>
              <Label>Admin Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Add notes about this invoice (e.g., reason for price change)..."
                rows={3}
              />
            </div>

            {invoice.type !== 'enrollment' && (
              <div>
                <Label>Stripe Payment Intent ID</Label>
                <Input
                  value={form.stripe_payment_intent_id}
                  onChange={(e) => setForm({ ...form, stripe_payment_intent_id: e.target.value })}
                  placeholder="pi_..."
                  className="font-mono text-xs"
                  data-testid="edit-invoice-stripe-pi"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Link this invoice to its Stripe payment (for older records paid
                  before auto-linking). Leave blank / clear to unlink.
                </p>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            // iter308n — explicit arrow wrapper. `onClick={handleSubmit}`
            // passed the React SyntheticEvent as `forceOverridePaid`,
            // which became a non-bool truthy value in the request body
            // and tripped `JSON.stringify` (circular refs) — surfacing
            // as a generic "Failed to update invoice" toast on every
            // attempt to edit pricing.
            onClick={() => handleSubmit()}
            disabled={processing}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="edit-invoice-save-btn"
          >
            {processing ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
            ) : (
              <><CheckCircle className="h-4 w-4 mr-2" />Save Changes</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
